import { createSelector } from 'reselect';
import _ from 'lodash';
import dayjs from '../../../../utils/dayjs';

export const userInfoSelector = state => state.userReducer.userInfo;

export const userNotificationsSelector = createSelector(userInfoSelector, userInfo =>
  userInfo ? userInfo.notifications : null
);

export const showSmsSettingBannerSelector = createSelector(
  userNotificationsSelector,
  notifications => (notifications ? notifications.find(n => n.type === 1)?.isDisplay : null) // type 1 is for SMS setting banner Id
);

export const showNotificationSelector = state => state.userReducer.showNotification;

export const userIdSelector = createSelector(userInfoSelector, userInfo => (userInfo ? userInfo.id : null));

export const userAvatarSelector = createSelector(userInfoSelector, userInfo => (userInfo ? userInfo.avatarUrl : null));

export const personNameSelector = createSelector(userInfoSelector, userInfo =>
  userInfo ? `${userInfo.firstName} ${userInfo.lastName}` : ''
);

export const organisationUserRoleSelector = createSelector(
  userInfoSelector,
  state => state.organisationReducer.businessInfo,
  (userInfo, businessInfo) => {
    if (userInfo && userInfo.organisationUserRoles && businessInfo) {
      const organisationUser = userInfo.organisationUserRoles.find(
        item => item.organisationId === businessInfo.organisationId
      );
      return organisationUser ? organisationUser.roleName.toLowerCase() : null;
    }
    return null;
  }
);

export const subscriptionsSelector = state => state.userReducer.subscriptions;

export const userSubscriptionsSelector = createSelector(subscriptionsSelector, subscriptions =>
  subscriptions
    ? _.chain(subscriptions.subscriptions)
        .map(item => {
          const activeDate = item.activeDate ? dayjs(item.activeDate).format('DD/MM/YYYY') : null;
          const exactExpiryDate = item.expiryDate ? item.expiryDate : null;
          let isExpired = false;
          let expiryDate = null;

          if (item.status === 'Expired') {
            isExpired = true;
            expiryDate = item.expiryDate ? dayjs(item.expiryDate).format('DD/MM/YYYY') : null;
          } else if (typeof item.isExpired === 'boolean') {
            isExpired = item.isExpired;
            expiryDate = item.expiryDate ? dayjs(item.expiryDate).format('DD/MM/YYYY') : null;
          } else if (!item.promoCodeId && !item.isRevoked) {
            if (!item.expiryDate) {
              if (item.planId && item.activeDate) {
                expiryDate = dayjs(item.activeDate)
                  .add(1, 'year')
                  .format('DD/MM/YYYY');
                const currentDate = dayjs();
                isExpired = dayjs(item.activeDate)
                  .add(1, 'year')
                  .isBefore(currentDate);
              }
            } else {
              expiryDate = dayjs(item.expiryDate).format('DD/MM/YYYY');
              const currentDate = dayjs();
              isExpired = dayjs(item.expiryDate).isBefore(currentDate);
            }
          }

          return { ...item, activeDate, expiryDate, exactExpiryDate, isExpired };
        })
        .value()
    : undefined
);

export const expiredUserSubscriptionsCountSelector = createSelector(
  subscriptionsSelector,
  userSubscriptionsSelector,
  (subscriptions, subscriptionList) => {
    if (subscriptions?.summary?.expired != null) {
      return subscriptions.summary.expired;
    }
    if (!subscriptionList) return 0;
    return subscriptionList.filter(item => item.isExpired).length;
  }
);

export const userSubscriptionsSummarySelector = createSelector(subscriptionsSelector, subscriptions => {
  if (!subscriptions?.summary) return null;
  const { total, remaining, expired } = subscriptions.summary;
  return {
    total: total ?? 0,
    remaining: remaining ?? 0,
    expired: expired ?? 0,
  };
});

export const loadingUserSubscriptionsSelector = state => state.userReducer.loadingUserSubscriptions;
